// Builds a MeridianLink SmartAPI "Consumer Credit - Order New" request (MISMO
// 3.4 + MCL extension) from the loan's borrowers. The backend adds the auth
// headers and POSTs it; the response comes back as native CREDIT_LIABILITY XML
// which fromCreditResponseXml() parses into the Debt page.

function esc(value) {
  return String(value === undefined || value === null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

// MISMO 3.4 NAME requires this child order: FirstName, LastName, MiddleName,
// SuffixName (the demo rejects "Request XML validation failed ... invalid child
// element 'LastName'" if MiddleName comes before LastName).
function nameXml(borrower) {
  const middle = borrower.middle_name
    ? `<MiddleName>${esc(borrower.middle_name)}</MiddleName>`
    : "";
  const suffix = borrower.suffix
    ? `<SuffixName>${esc(borrower.suffix)}</SuffixName>`
    : "";
  return `<NAME>
    <FirstName>${esc(borrower.first_name)}</FirstName>
    <LastName>${esc(borrower.last_name)}</LastName>
    ${middle}
    ${suffix}
  </NAME>`;
}

function partyXml(borrower, n) {
  const ssn = String(borrower.ssn || "").replace(/\D/g, "");
  return `<PARTY p2:label="Party${n}">
  <INDIVIDUAL>${nameXml(borrower)}</INDIVIDUAL>
  <ROLES><ROLE><BORROWER><RESIDENCES><RESIDENCE>
    <ADDRESS>
      <AddressLineText>${esc(borrower.current_address)}</AddressLineText>
      <CityName>${esc(borrower.city)}</CityName>
      <CountryCode>US</CountryCode>
      <PostalCode>${esc(borrower.zip_code)}</PostalCode>
      <StateCode>${esc(borrower.state)}</StateCode>
    </ADDRESS>
    <RESIDENCE_DETAIL><BorrowerResidencyType>Current</BorrowerResidencyType></RESIDENCE_DETAIL>
  </RESIDENCE></RESIDENCES></BORROWER>
  <ROLE_DETAIL><PartyRoleType>Borrower</PartyRoleType></ROLE_DETAIL></ROLE></ROLES>
  <TAXPAYER_IDENTIFIERS><TAXPAYER_IDENTIFIER>
    <TaxpayerIdentifierType>SocialSecurityNumber</TaxpayerIdentifierType>
    <TaxpayerIdentifierValue>${esc(ssn)}</TaxpayerIdentifierValue>
  </TAXPAYER_IDENTIFIER></TAXPAYER_IDENTIFIERS>
</PARTY>`;
}

/**
 * Optional payment card (SERVICE_PAYMENTS). MeridianLink's demo will not
 * process an order without one; without it the response comes back with no
 * tradelines. card = { name, number, expMonth, expYear, cvv, address, city,
 * state, zip }.
 */
function servicePaymentsXml(card) {
  if (!card || !card.number) return "";
  const digits = String(card.number || "").replace(/\D/g, "");
  const mm = String(card.expMonth || "").padStart(2, "0");
  const yyyy = String(card.expYear || "");
  const exp = mm && yyyy ? `${yyyy}-${mm}` : "";
  const first = card.name ? String(card.name).trim().split(/\s+/)[0] : "";
  const last = card.name
    ? String(card.name)
        .trim()
        .split(/\s+/)
        .slice(1)
        .join(" ")
    : "";
  return `
      <SERVICE_PAYMENTS><SERVICE_PAYMENT>
        <ADDRESS>
          <AddressLineText>${esc(card.address)}</AddressLineText>
          <CityName>${esc(card.city)}</CityName>
          <PostalCode>${esc(card.zip)}</PostalCode>
          <StateCode>${esc(card.state)}</StateCode>
        </ADDRESS>
        <NAME>
          <FirstName>${esc(first)}</FirstName>
          <LastName>${esc(last)}</LastName>
        </NAME>
        <SERVICE_PAYMENT_DETAIL>
          <ServicePaymentAccountIdentifier>${esc(
            digits
          )}</ServicePaymentAccountIdentifier>
          <ServicePaymentCreditAccountExpirationDate>${esc(
            exp
          )}</ServicePaymentCreditAccountExpirationDate>
          <ServicePaymentSecondaryCreditAccountIdentifier>${esc(
            card.cvv
          )}</ServicePaymentSecondaryCreditAccountIdentifier>
        </SERVICE_PAYMENT_DETAIL>
      </SERVICE_PAYMENT></SERVICE_PAYMENTS>`;
}

/**
 * @param borrowers  the loan's borrowers (applicant.borrowers)
 * @param options    { joint, bureaus:{equifax,experian,transunion}, card }
 */
export function toCreditRequestXml(borrowers, options) {
  const opts = options || {};
  const bureaus = opts.bureaus || {};
  const list =
    opts.joint && borrowers.length > 1 ? borrowers : borrowers.slice(0, 1);

  const payments = servicePaymentsXml(opts.card);
  const parties = list.map((b, i) => partyXml(b, i + 1)).join("\n");
  const arcs = list
    .map(
      (_b, i) =>
        `<RELATIONSHIP p2:arcrole="urn:fdc:Meridianlink.com:2017:mortgage/PARTY_IsVerifiedBy_SERVICE" p2:from="Party${i +
          1}" p2:to="Service1"/>`
    )
    .join("\n");

  return `<?xml version="1.0" encoding="utf-8"?>
<MESSAGE xmlns="http://www.mismo.org/residential/2009/schemas" xmlns:p2="http://www.w3.org/1999/xlink" xmlns:p3="inetapi/MISMO3_4_MCL_Extension.xsd" MessageType="Request">
  <ABOUT_VERSIONS><ABOUT_VERSION><DataVersionIdentifier>201703</DataVersionIdentifier></ABOUT_VERSION></ABOUT_VERSIONS>
  <DEAL_SETS><DEAL_SET><DEALS><DEAL>
    <PARTIES>
${parties}
    </PARTIES>
    <RELATIONSHIPS>
${arcs}
    </RELATIONSHIPS>
    <SERVICES><SERVICE p2:label="Service1">
      <CREDIT><CREDIT_REQUEST><CREDIT_REQUEST_DATAS><CREDIT_REQUEST_DATA>
        <CREDIT_REPOSITORY_INCLUDED>
          <CreditRepositoryIncludedEquifaxIndicator>${!!bureaus.equifax}</CreditRepositoryIncludedEquifaxIndicator>
          <CreditRepositoryIncludedExperianIndicator>${!!bureaus.experian}</CreditRepositoryIncludedExperianIndicator>
          <CreditRepositoryIncludedTransUnionIndicator>${!!bureaus.transunion}</CreditRepositoryIncludedTransUnionIndicator>
        </CREDIT_REPOSITORY_INCLUDED>
        <CREDIT_REQUEST_DATA_DETAIL><CreditReportRequestActionType>Submit</CreditReportRequestActionType></CREDIT_REQUEST_DATA_DETAIL>
      </CREDIT_REQUEST_DATA></CREDIT_REQUEST_DATAS></CREDIT_REQUEST></CREDIT>${payments}
      <SERVICE_PRODUCT><SERVICE_PRODUCT_REQUEST><SERVICE_PRODUCT_DETAIL>
        <ServiceProductDescription>CreditOrder</ServiceProductDescription>
        <EXTENSION><OTHER>
          <p3:SERVICE_PREFERRED_RESPONSE_FORMATS><p3:SERVICE_PREFERRED_RESPONSE_FORMAT><p3:SERVICE_PREFERRED_RESPONSE_FORMAT_DETAIL>
            <p3:PreferredResponseFormatType>Xml</p3:PreferredResponseFormatType>
          </p3:SERVICE_PREFERRED_RESPONSE_FORMAT_DETAIL></p3:SERVICE_PREFERRED_RESPONSE_FORMAT></p3:SERVICE_PREFERRED_RESPONSE_FORMATS>
        </OTHER></EXTENSION>
      </SERVICE_PRODUCT_DETAIL></SERVICE_PRODUCT_REQUEST></SERVICE_PRODUCT>
    </SERVICE></SERVICES>
  </DEAL></DEALS></DEAL_SET></DEAL_SETS>
</MESSAGE>`;
}

// Lighter PARTY for a StatusQuery/retrieve (no residence or service payment
// needed - just enough identity for the vendor to match the file).
function statusPartyXml(borrower, n) {
  const ssn = String(borrower.ssn || "").replace(/\D/g, "");
  return `<PARTY p2:label="Party${n}">
  <INDIVIDUAL>${nameXml(borrower)}</INDIVIDUAL>
  <ROLES><ROLE><ROLE_DETAIL><PartyRoleType>Borrower</PartyRoleType></ROLE_DETAIL></ROLE></ROLES>
  <TAXPAYER_IDENTIFIERS><TAXPAYER_IDENTIFIER>
    <TaxpayerIdentifierType>SocialSecurityNumber</TaxpayerIdentifierType>
    <TaxpayerIdentifierValue>${esc(ssn)}</TaxpayerIdentifierValue>
  </TAXPAYER_IDENTIFIER></TAXPAYER_IDENTIFIERS>
</PARTY>`;
}

/**
 * Builds the StatusQuery ("retrieve") request used to poll an order until it is
 * READY. The order is asynchronous: the initial Submit returns a
 * VendorOrderIdentifier with status NOT READY, and this call (repeated) fetches
 * the finished report. Requests both Xml (to import) and Pdf (to print/save).
 *
 * @param borrowers  same borrowers used for the Submit
 * @param options    { joint, bureaus, orderId }
 */
export function toCreditStatusQueryXml(borrowers, options) {
  const opts = options || {};
  const bureaus = opts.bureaus || {};
  const list =
    opts.joint && borrowers.length > 1 ? borrowers : borrowers.slice(0, 1);

  const parties = list.map((b, i) => statusPartyXml(b, i + 1)).join("\n");
  const arcs = list
    .map(
      (_b, i) =>
        `<RELATIONSHIP p2:arcrole="urn:fdc:Meridianlink.com:2017:mortgage/PARTY_IsVerifiedBy_SERVICE" p2:from="Party${i +
          1}" p2:to="Service1"/>`
    )
    .join("\n");

  return `<?xml version="1.0" encoding="utf-8"?>
<MESSAGE xmlns="http://www.mismo.org/residential/2009/schemas" xmlns:p2="http://www.w3.org/1999/xlink" xmlns:p3="inetapi/MISMO3_4_MCL_Extension.xsd" MessageType="Request">
  <ABOUT_VERSIONS><ABOUT_VERSION><DataVersionIdentifier>201703</DataVersionIdentifier></ABOUT_VERSION></ABOUT_VERSIONS>
  <DEAL_SETS><DEAL_SET><DEALS><DEAL>
    <PARTIES>
${parties}
    </PARTIES>
    <RELATIONSHIPS>
${arcs}
    </RELATIONSHIPS>
    <SERVICES><SERVICE p2:label="Service1">
      <CREDIT><CREDIT_REQUEST><CREDIT_REQUEST_DATAS><CREDIT_REQUEST_DATA>
        <CREDIT_REPOSITORY_INCLUDED>
          <CreditRepositoryIncludedEquifaxIndicator>${!!bureaus.equifax}</CreditRepositoryIncludedEquifaxIndicator>
          <CreditRepositoryIncludedExperianIndicator>${!!bureaus.experian}</CreditRepositoryIncludedExperianIndicator>
          <CreditRepositoryIncludedTransUnionIndicator>${!!bureaus.transunion}</CreditRepositoryIncludedTransUnionIndicator>
        </CREDIT_REPOSITORY_INCLUDED>
        <CREDIT_REQUEST_DATA_DETAIL><CreditReportRequestActionType>StatusQuery</CreditReportRequestActionType></CREDIT_REQUEST_DATA_DETAIL>
      </CREDIT_REQUEST_DATA></CREDIT_REQUEST_DATAS></CREDIT_REQUEST></CREDIT>
      <SERVICE_PRODUCT><SERVICE_PRODUCT_REQUEST><SERVICE_PRODUCT_DETAIL>
        <ServiceProductDescription>CreditOrder</ServiceProductDescription>
        <EXTENSION><OTHER>
          <p3:SERVICE_PREFERRED_RESPONSE_FORMATS>
            <p3:SERVICE_PREFERRED_RESPONSE_FORMAT><p3:SERVICE_PREFERRED_RESPONSE_FORMAT_DETAIL><p3:PreferredResponseFormatType>Xml</p3:PreferredResponseFormatType></p3:SERVICE_PREFERRED_RESPONSE_FORMAT_DETAIL></p3:SERVICE_PREFERRED_RESPONSE_FORMAT>
            <p3:SERVICE_PREFERRED_RESPONSE_FORMAT><p3:SERVICE_PREFERRED_RESPONSE_FORMAT_DETAIL><p3:PreferredResponseFormatType>Pdf</p3:PreferredResponseFormatType></p3:SERVICE_PREFERRED_RESPONSE_FORMAT_DETAIL></p3:SERVICE_PREFERRED_RESPONSE_FORMAT>
          </p3:SERVICE_PREFERRED_RESPONSE_FORMATS>
        </OTHER></EXTENSION>
      </SERVICE_PRODUCT_DETAIL></SERVICE_PRODUCT_REQUEST></SERVICE_PRODUCT>
      <SERVICE_PRODUCT_FULFILLMENT><SERVICE_PRODUCT_FULFILLMENT_DETAIL>
        <VendorOrderIdentifier>${esc(opts.orderId)}</VendorOrderIdentifier>
      </SERVICE_PRODUCT_FULFILLMENT_DETAIL></SERVICE_PRODUCT_FULFILLMENT>
    </SERVICE></SERVICES>
  </DEAL></DEALS></DEAL_SET></DEAL_SETS>
</MESSAGE>`;
}
